/**
 * Offline mode: service worker registration, "Save for offline" job downloads (Cache Storage),
 * and the daily report outbox (IndexedDB) that syncs itself when signal comes back.
 */
import { useEffect, useState } from "react";
import type { DailyReport, Pin } from "@centerline/core/viewer";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import { api, ApiError, identity, type DocumentSummary } from "./api";

export const PLAN_CACHE = "cl-plans";
const API_CACHE = "cl-api-v1";

export const newUuid = (): string =>
  typeof crypto.randomUUID === "function" ? crypto.randomUUID()
    : "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (c) => (Number(c) ^ (crypto.getRandomValues(new Uint8Array(1))[0]! & (15 >> (Number(c) / 4)))).toString(16));

export const isNetworkError = (e: unknown) => !(e instanceof ApiError) || e.status === 0 || e.status >= 502;

export function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return;
  window.addEventListener("load", () => { navigator.serviceWorker.register("/sw.js").then(() => navigator.serviceWorker.ready).then(() => cacheAppShell()).catch(() => {}); });
}

// ---------- Save for offline ----------
export interface SavedSet { projectId: string; docs: Array<{ id: string; name: string; version: number; bytes: number }>; bytes: number; savedAt: string }
const savedKey = (projectId: string) => `cl.offline.${projectId}`;
export const savedSet = (projectId: string): SavedSet | null => { try { return JSON.parse(localStorage.getItem(savedKey(projectId)) ?? "null"); } catch { return null; } };

export async function storageInfo(): Promise<{ usage: number; quota: number; persisted: boolean }> {
  const est = (await navigator.storage?.estimate?.().catch(() => null)) ?? {};
  const persisted = (await navigator.storage?.persisted?.().catch(() => false)) ?? false;
  return { usage: est.usage ?? 0, quota: est.quota ?? 0, persisted };
}
export const mb = (n: number) => `${(n / 1024 / 1024).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;

/** Sets to keep offline: the working sets, or every active set when none is marked working. */
export const offlineDocs = (docs: DocumentSummary[]) => { const live = docs.filter((d) => !d.archivedAt); const w = live.filter((d) => d.working); return w.length ? w : live; };

async function sizeOf(url: string): Promise<number> {
  try { const r = await fetch(url, { method: "HEAD", credentials: "same-origin" }); return Number(r.headers.get("content-length") ?? 0); } catch { return 0; }
}

/** Checks space first; returns a warning (string) instead of downloading when the set probably won't fit. */
export async function planOfflineSize(docs: DocumentSummary[]): Promise<{ bytes: number; warning: string | null }> {
  let bytes = 0;
  for (const d of docs) bytes += await sizeOf(api.planUrl(d.id));
  bytes += docs.reduce((n, d) => n + d.pageCount * 25_000, 0);
  const { usage, quota } = await storageInfo();
  const free = quota ? quota - usage : Infinity;
  const warning = bytes > free * 0.8 ? `This job needs about ${mb(bytes)} but the device only allows about ${mb(Math.max(0, free))} more. Free up space or mark fewer working sets.`
    : bytes > 400 * 1024 * 1024 ? `This job is about ${mb(bytes)}. iPads may clear very large offline data; consider marking fewer working sets.` : null;
  return { bytes, warning };
}

/** The app itself (page, scripts, styles, pdf.js worker) so it opens with no signal even on the first visit. */
export async function cacheAppShell() {
  if (!("caches" in window)) return;
  const shell = await caches.open("cl-shell-v1"), assets = await caches.open("cl-assets-v1");
  try { const r = await fetch("/", { cache: "no-store", credentials: "same-origin" }); if (r.ok) await shell.put("/", r); } catch { /* offline */ }
  const urls = new Set<string>([new URL(workerUrl, location.href).pathname]);
  for (const el of Array.from(document.querySelectorAll<HTMLScriptElement | HTMLLinkElement>("script[src], link[rel=stylesheet], link[rel=modulepreload]"))) {
    const u = new URL((el as HTMLScriptElement).src || (el as HTMLLinkElement).href, location.href);
    if (u.origin === location.origin && u.pathname.startsWith("/assets/")) urls.add(u.pathname);
  }
  for (const u of urls) { if (!u.startsWith("/assets/") || (await assets.match(u))) continue; try { const r = await fetch(u); if (r.ok) await assets.put(u, r); } catch { /* offline */ } }
}

export async function saveForOffline(projectId: string, docs: DocumentSummary[], onProgress: (done: number, total: number, label: string) => void): Promise<SavedSet> {
  await navigator.storage?.persist?.().catch(() => false);
  await cacheAppShell();
  const plans = await caches.open(PLAN_CACHE), apiCache = await caches.open(API_CACHE);
  const json: string[] = ["/api/auth/me", "/api/projects", `/api/projects/${projectId}`, `/api/projects/${projectId}/sov`, "/api/phase-codes", `/api/projects/${projectId}/takeoff-items`, `/api/projects/${projectId}/pins`, `/api/projects/${projectId}/punch-list`, `/api/daily-reports?projectId=${projectId}`];
  for (const d of docs) json.push(`/api/documents/${d.id}`, `/api/documents/${d.id}/authors`, `/api/projects/${projectId}/pins?documentId=${encodeURIComponent(d.id)}`);
  const thumbs = docs.flatMap((d) => Array.from({ length: d.pageCount }, (_, i) => `/api/documents/${d.id}/thumbs/${i + 1}.jpg`));
  const total = json.length + docs.length + thumbs.length;
  let done = 0;
  const step = (label: string) => onProgress(++done, total, label);
  const get = (url: string) => fetch(url, { credentials: "same-origin", headers: identity() });
  for (const u of json) { try { const r = await get(u); if (r.ok) await apiCache.put(u, r); } catch { /* keep going */ } step("Job data"); }
  const out: SavedSet = { projectId, docs: [], bytes: 0, savedAt: new Date().toISOString() };
  for (const d of docs) {
    const r = await get(api.planUrl(d.id));
    if (!r.ok) throw new Error(`Couldn't download ${d.name} (${r.status}).`);
    const blob = await r.blob();
    await plans.put(api.planUrl(d.id), new Response(blob, { headers: { "content-type": "application/pdf", "content-length": String(blob.size) } }));
    out.docs.push({ id: d.id, name: d.name, version: d.version, bytes: blob.size }); out.bytes += blob.size;
    step(`Plans: ${d.name}`);
  }
  let i = 0;
  const worker = async () => { while (i < thumbs.length) { const u = thumbs[i++]!; try { const r = await get(u); if (r.ok) { const b = await r.blob(); out.bytes += b.size; await apiCache.put(u, new Response(b, { headers: { "content-type": "image/jpeg" } })); } } catch { /* thumbnails are optional */ } step("Sheet thumbnails"); } };
  await Promise.all([worker(), worker(), worker(), worker()]);
  localStorage.setItem(savedKey(projectId), JSON.stringify(out));
  return out;
}

export async function removeOffline(projectId: string) {
  const s = savedSet(projectId);
  if (s) { const plans = await caches.open(PLAN_CACHE); for (const d of s.docs) await plans.delete(api.planUrl(d.id)); }
  localStorage.removeItem(savedKey(projectId));
}

/** Saved plan bytes for the viewer (null when the set isn't saved). */
export async function cachedPlan(docId: string): Promise<Uint8Array | null> {
  if (!("caches" in window)) return null;
  const r = await (await caches.open(PLAN_CACHE)).match(api.planUrl(docId));
  return r ? new Uint8Array(await r.arrayBuffer()) : null;
}

/** Signing out on a shared iPad: drop saved job data (unsynced reports stay until they sync). */
export async function clearOfflineData() {
  if ("caches" in window) { await caches.delete(PLAN_CACHE); await caches.delete(API_CACHE); }
  for (const k of Object.keys(localStorage)) if (k.startsWith("cl.offline.") || k === ME_KEY) localStorage.removeItem(k);
}

// ---------- Remembered sign-in (so no signal doesn't look like signed out) ----------
const ME_KEY = "cl.me";
export const rememberMe = (me: unknown) => localStorage.setItem(ME_KEY, JSON.stringify(me));
export const rememberedMe = <T,>(): T | null => { try { return JSON.parse(localStorage.getItem(ME_KEY) ?? "null"); } catch { return null; } };

// ---------- Daily report outbox ----------
export interface QueuedPhoto { id: string; caption: string; name: string; type: string; blob: Blob }
export interface QueuedReport {
  id: string;
  /** Server version this edit was based on; null for a report the server hasn't seen yet. */
  baseVersion: number | null;
  fields: Partial<DailyReport>;
  status: "draft" | "submitted";
  photos: QueuedPhoto[];
  queuedAt: string;
  error?: string;
  /** Photo pin waiting to sync (same outbox, same client-id retries). Absent = daily report. */
  kind?: "pin" | "pinpatch";
  projectId?: string;
  pin?: Pick<Pin, "documentId" | "pageId" | "x" | "y" | "title" | "description" | "createdBy"> & Partial<Pick<Pin, "type" | "assignee" | "dueDate" | "status">> & { createdById?: string };
  /** For kind "pinpatch": the pin being edited and the changed fields (last write wins on the server). */
  pinId?: string;
  patch?: Partial<Pin>;
}

let dbp: Promise<IDBDatabase> | null = null;
function db(): Promise<IDBDatabase> {
  return (dbp ??= new Promise((resolve, reject) => {
    const r = indexedDB.open("centerline-offline", 1);
    r.onupgradeneeded = () => r.result.createObjectStore("outbox", { keyPath: "id" });
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  }));
}
async function tx<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction("outbox", mode);
    const req = fn(t.objectStore("outbox"));
    t.oncomplete = () => resolve(req.result);
    t.onerror = () => reject(t.error);
  });
}
export const queuedReports = () => tx<QueuedReport[]>("readonly", (s) => s.getAll() as IDBRequest<QueuedReport[]>);
export const queuedReport = (id: string) => tx<QueuedReport | undefined>("readonly", (s) => s.get(id) as IDBRequest<QueuedReport | undefined>);

export async function queueReport(item: Omit<QueuedReport, "queuedAt" | "photos"> & { photos?: QueuedPhoto[] }) {
  const prev = await queuedReport(item.id);
  const photos = [...(prev?.photos ?? []), ...(item.photos ?? [])].filter((p, i, a) => a.findIndex((q) => q.id === p.id) === i);
  await tx("readwrite", (s) => s.put({ ...item, baseVersion: prev ? prev.baseVersion : item.baseVersion, photos, queuedAt: new Date().toISOString() }));
  emit();
  void syncNow();
}
/** Queues a new Photo pin (made with no signal) with its photos. */
export async function queuePin(id: string, projectId: string, pin: NonNullable<QueuedReport["pin"]>, photos: QueuedPhoto[]) {
  await tx("readwrite", (s) => s.put({ id, kind: "pin", projectId, pin, photos, baseVersion: null, fields: {}, status: "draft", queuedAt: new Date().toISOString() } satisfies QueuedReport));
  emit();
  void syncNow();
}
/** Queues an edit to a pin made with no signal. If the pin itself is still waiting to sync, the edit is folded into it. */
export async function queuePinPatch(pinId: string, projectId: string, patch: Partial<Pin>) {
  const waiting = await queuedReport(pinId);
  if (waiting?.kind === "pin") {
    await tx("readwrite", (s) => s.put({ ...waiting, pin: { ...waiting.pin!, ...patch } as QueuedReport["pin"], queuedAt: new Date().toISOString() }));
  } else {
    const id = `patch-${pinId}`;
    const prev = await queuedReport(id);
    await tx("readwrite", (s) => s.put({ id, kind: "pinpatch", projectId, pinId, patch: { ...(prev?.patch ?? {}), ...patch }, photos: [], baseVersion: null, fields: {}, status: "draft", queuedAt: new Date().toISOString() } satisfies QueuedReport));
  }
  emit();
  void syncNow();
}
export const queuedPins = async (documentId?: string) => (await queuedReports().catch(() => [] as QueuedReport[])).filter((q) => q.kind === "pin" && (!documentId || q.pin?.documentId === documentId));
export const dropQueued = (id: string) => unqueue(id);
const unqueue = async (id: string) => { await tx("readwrite", (s) => s.delete(id)); emit(); };

// ---------- Status + sync ----------
export interface OfflineStatus { online: boolean; waiting: number; syncing: boolean; error: string | null }
let status: OfflineStatus = { online: navigator.onLine, waiting: 0, syncing: false, error: null };
const subs = new Set<(s: OfflineStatus) => void>();
const setStatus = (p: Partial<OfflineStatus>) => { status = { ...status, ...p }; subs.forEach((f) => f(status)); };
function emit() { queuedReports().then((q) => setStatus({ waiting: q.length, error: q.find((x) => x.error)?.error ?? null })).catch(() => {}); }

export function useOfflineStatus() {
  const [s, set] = useState(status);
  useEffect(() => { subs.add(set); set(status); return () => { subs.delete(set); }; }, []);
  return s;
}

async function sendPin(q: QueuedReport) {
  let pin = (await api.createPin(q.projectId!, { type: "Photo", ...q.pin!, id: q.id } as Partial<Pin>)).pin;
  for (const p of q.photos) {
    if (pin.attachments.some((a) => a.id === p.id)) continue;
    const form = new FormData();
    form.append("attachmentId", p.id); form.append("file", p.blob, p.name);
    const res = await fetch(`/api/pins/${q.id}/attachments`, { method: "POST", body: form, credentials: "same-origin", headers: identity() });
    if (res.status === 401) throw new ApiError(401, "Sign in again to sync.");
    if (!res.ok) throw new ApiError(res.status, (await res.json().catch(() => ({}))).message ?? `Photo upload failed (${res.status})`);
    pin = (await res.json()).pin;
  }
  return pin;
}

async function sendOne(q: QueuedReport) {
  if (q.kind === "pin") return sendPin(q);
  if (q.kind === "pinpatch") return (await api.updatePin(q.pinId!, q.patch!)).pin;
  let r: DailyReport;
  if (q.baseVersion === null) {
    const res = (await api.createDailyReport({ ...q.fields, id: q.id })) as { report: DailyReport; duplicate?: boolean };
    r = res.report;
    if (res.duplicate && r.status === "draft") r = (await api.updateDailyReport(q.id, { ...q.fields, version: r.version })).report;
  } else {
    r = (await api.dailyReport(q.id)).report;
    if (r.status === "draft") r = (await api.updateDailyReport(q.id, { ...q.fields, version: r.version })).report;
  }
  for (const p of q.photos) {
    if (r.photos.some((x) => x.id === p.id)) continue;
    const form = new FormData();
    form.append("caption", p.caption); form.append("photoId", p.id); form.append("photo", p.blob, p.name);
    const res = await fetch(`/api/daily-reports/${q.id}/photos`, { method: "POST", body: form, credentials: "same-origin", headers: identity() });
    if (res.status === 401) throw new ApiError(401, "Sign in again to sync.");
    if (!res.ok && res.status !== 200) throw new ApiError(res.status, (await res.json().catch(() => ({}))).message ?? `Photo upload failed (${res.status})`);
    r = (await res.json()).report;
  }
  if (q.status === "submitted" && r.status === "draft") r = (await api.updateDailyReport(q.id, { version: r.version, status: "submitted" })).report;
  return r;
}

let running: Promise<void> | null = null;
export function syncNow(): Promise<void> {
  if (running) return running;
  running = (async () => {
    const list = await queuedReports().catch(() => [] as QueuedReport[]);
    if (!list.length || !navigator.onLine) return;
    setStatus({ syncing: true });
    const rank = (k?: string) => (k === "pin" ? 0 : k === "pinpatch" ? 1 : 2);
    list.sort((a, b) => rank(a.kind) - rank(b.kind));
    for (const q of list) {
      try { await sendOne(q); await unqueue(q.id); window.dispatchEvent(new CustomEvent("cl:synced", { detail: q.id })); }
      catch (e) {
        if (isNetworkError(e)) break;
        if (e instanceof ApiError && e.status === 401) { setStatus({ error: "Sign in again to sync." }); break; }
        await tx("readwrite", (s) => s.put({ ...q, error: (e as Error).message }));
      }
    }
  })().finally(() => { running = null; setStatus({ syncing: false }); emit(); });
  return running;
}

export function startOfflineSync() {
  const on = () => { setStatus({ online: navigator.onLine }); if (navigator.onLine) void syncNow(); };
  window.addEventListener("online", on);
  window.addEventListener("offline", on);
  window.setInterval(() => { if (status.waiting && navigator.onLine) void syncNow(); }, 30_000);
  emit();
  void syncNow();
}
